import csiCard, { CardSpecs } from "csi/card/_base";
import NodeDataStore from "csi/datastore/NodeDataStore";
import { getSearch } from "csi/service/dataStoreService";
import { RightPaneComponent, RightPaneComponentAlwaysAllowed } from "csi/state/rightPane";
import { C8Node, cnl, ICache } from "csi/typings";
import xml from "csi/xml";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { getSetEditStore, PRODUCT_PRESENTATION_SET } from "../PPService";
import SetEditStore from "./SetEditStore";

class PresentationSetsRightPaneComponent extends RightPaneComponent {
	@observable.ref private _editSetStore: SetEditStore;
	@observable private _colorUrl: cnl;
	@observable private _productUrl: cnl;
	@observable private _searchText = "";
	@observable private _showBackButton = true;
	readonly onSaveSet: VoidFunction;

	/** cache */
	readonly cache: ICache;

	/** presentation url */
	readonly presentationUrl: cnl;

	/** attribute card specs for set */
	readonly setSpecs: CardSpecs;

	/** selected color to add to set once picked */
	get color(): C8Node {
		return this.cache.get(this._colorUrl);
	}

	/** color specification node for selected color */
	get colorSpec(): C8Node {
		const { cache, color } = this;
		return cache.get(color?.ColorSpecification);
	}

	/** store for editing selected set */
	get editSetStore(): SetEditStore {
		return this._editSetStore;
	}

	/** indicates if it's the default chip */
	get isDefaultChip(): boolean {
		return this._productUrl === this._colorUrl;
	}

	/** indicate if show the back button */
	get showBackButton(): boolean {
		return this._showBackButton && !!this.editSetStore;
	}

	/** selected product to add to set once picked */
	get product(): C8Node {
		return this.cache.get(this._productUrl);
	}

	/** selected product type */
	get productType(): string {
		return this.product.$Type;
	}

	/** data store for sets in presentation that meet search criteria */
	@computed get setsStore(): NodeDataStore {
		const { cache, presentationUrl, setSpecs, _searchText } = this;
		const paths = csiCard.getPathsForSpecs(setSpecs, null, null, true);
		const search = getSearch("ProductPresentationSet.ProductPresentation", presentationUrl);
		search.addDependencyPaths(paths);
		search.getSortQuery().SortByNode(xml.PROP_NAME);
		csiCard.getQueryForSpecs(setSpecs, _searchText, search.getQuery());
		return new NodeDataStore(search, cache);
	}

	/** reset the selected set */
	@action onBack = (): void => {
		this._editSetStore = null;
	};

	/** set search text */
	@action setSearchText = (text: string): void => {
		this._searchText = text;
	};

	/** select a set; if no set given, then creating a new one */
	selectSet = async (setUrl?: cnl, ignoreTheSelectedItem: boolean = false): Promise<void> => {
		const { cache, presentationUrl, _colorUrl, _productUrl } = this;
		const store = await getSetEditStore(presentationUrl, setUrl, cache);

		runInAction(() => {
			if (!ignoreTheSelectedItem) {
				store.addItem(_productUrl, _colorUrl);
			}

			this._showBackButton = !ignoreTheSelectedItem;
			this._editSetStore = store;
		});
	};

	/** set product and color to add to set once one is selected */
	@action setProductAndColor = (productUrl: cnl, colorUrl: cnl): void => {
		this._colorUrl = colorUrl;
		this._productUrl = productUrl;
	};

	constructor(cache: ICache, presentationUrl: cnl, onLeave?: VoidFunction, onSaveSet?: VoidFunction) {
		super("PresentationSets", {
			isAllowed: RightPaneComponentAlwaysAllowed,
			closeOnTabChange: true,
			confirmLeaving: () => this._editSetStore?.hasChanged,
			onLeave,
			view: "ProductPresentation/PresentationSetsLayout",
			viewWidth: 320,
		});

		this.cache = cache;
		this.presentationUrl = presentationUrl;
		this.setSpecs = csiCard.getAttributeCardSlimSpecs(PRODUCT_PRESENTATION_SET);
		this.onSaveSet = onSaveSet;

		makeObservable(this);
	}
}

export default PresentationSetsRightPaneComponent;